import HttpClient from "../../auth/authApi";
import type Feature from "ol/Feature";
import { formatGeoServerViewParams } from "./geoServerRiskLayer";
import { getOverrideDatasetGeoLayers } from "./riskLayerCatalog";

/** Même relatif `/api` que `geoserver/wms` (proxy → GeoServer). */
export const GEOSERVER_WFS_URL = "geoserver/wfs";

export type FetchGeoServerFeatureByIdParams = {
  typeName: string;
  idValue: string | number;
  /** Colonne filtre côté PostGIS / GeoServer (défaut : `id`). */
  idProperty?: string;
  viewParams?: Record<string, string>;
  /** Sortie WFS ; à aligner avec la projection de la carte (OL `EPSG:3857`). */
  srsName?: string;
};

/**
 * WFS 2.0 GetFeature, un seul enregistrement, filtre CQL sur l’identifiant.
 * Retourne un GeoJSON `Feature` ou `null` si rien ne correspond.
 */
export async function fetchGeoServerFeatureJsonById(
  params: FetchGeoServerFeatureByIdParams,
): Promise<GeoJSON.Feature | null> {
  const idProp = params.idProperty ?? "id";
  const v = params.idValue;
  const literal =
    typeof v === "number"
      ? String(v)
      : typeof v === "string" && /^\d+(\.\d+)?$/.test(v)
        ? v
        : `'${String(v).replace(/'/g, "''")}'`;
  const cql_filter = `${idProp}=${literal}`;

  const query: Record<string, string> = {
    service: "WFS",
    version: "2.0.0",
    request: "GetFeature",
    typeNames: params.typeName,
    outputFormat: "application/json",
    srsName: params.srsName ?? "EPSG:3857",
    count: "1",
    cql_filter,
  };
  if (params.viewParams) {
    const viewparams = formatGeoServerViewParams(params.viewParams);
    if (viewparams) {
      query.VIEWPARAMS = viewparams;
    }
  }

  const response = await HttpClient.get(GEOSERVER_WFS_URL, { params: query });
  const fc = response.data as GeoJSON.FeatureCollection;
  if (!fc?.features?.length) {
    return null;
  }
  return fc.features[0] ?? null;
}

/** Lit `id` métier depuis les attributs MVT ; sinon fallback sur `getId()` (identifiant tuile MVT). */
export function resolveMvtFeatureIdForWfs(feature: Feature): string | number | null {
  const props = feature.getProperties();
  if (props.id !== undefined && props.id !== null) {
    return props.id as string | number;
  }
  if (props.Id !== undefined && props.Id !== null) {
    return props.Id as string | number;
  }
  const oid = feature.getId();
  if (oid !== undefined && oid !== null) {
    return oid as string | number;
  }
  return null;
}

/** Associe la couche MVT sélectionnée au `typeName` WFS (base vs overrides). */
export function resolveOverrideDatasetWfsTypeName(
  datasetKey: string,
  feature: Feature,
): string | null {
  const def = getOverrideDatasetGeoLayers(datasetKey);
  if (!def) {
    return null;
  }
  const label = String(feature.get("_mvtLayerLabel") ?? "");
  if (label.includes("persist")) {
    return def.overrides ?? null;
  }
  return def.base;
}
